/* global FB */
import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { EmailIcon, PhoneIcon, MyDetailsIcon, SignLanguageIcon } from '@boltui/icons'
import Accordion from '@boltui/accordion'
import marked from 'marked'
import * as actions from '../../actions'
import { doTrack } from '../../helpers/TaggingSerializer'

import Helmet from 'react-helmet'
import Alert from '@argos/alert'
import LoadingWheel from '@argos/loading-wheel'
import ChatLogo from '../../components/LiveChat/ChatLogo'
import CategoryMenu from '../../components/CategoryMenu/CategoryMenu'
import SearchBar from '../../components/SearchBar/SearchBar'
import Sidebar from '../../components/Sidebar/Sidebar'

import './ContactUs.scss'

class ContactUs extends PureComponent {
  static waitForDataServer (dispatch, params, req, res) {
    return Promise
      .all([
        dispatch(actions.fetchSidebarNav(true)),
        dispatch(actions.fetchAlertContent(true))
      ])
  }

  constructor () {
    super()

    this.state = {
      fbInit: false
    }
  }

  componentDidMount () {
    const { sidebar } = this.props

    if (!sidebar.length) this.props.actions.fetchSidebarNav()
    actions.fetchAlertContent()
    this.fireAnalytics()

    const conversocial = document.getElementById('conversocial')
    if (conversocial) {
      conversocial.setAttribute('page_id', '108712049190071')
      conversocial.setAttribute('ref', 'customerchat')
    } else {
      console.error('Unable to find conversocial element!')
    }
  }

  fireAnalytics = () => {
    if (window.digitalData) {
      window.digitalData.page.pageInfo.pageName = 'ar:help:contactUs:'
      window.digitalData.page.pageInfo.templateTitle = 'help_FAQ'
    }

    doTrack('help_initialLoad')
  }

  handleFacebookInit = () => {
    if (!this.state.fbInit) {
      FB.init({
        appId: '371291917550',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v2.10'
      })

      this.setState({ fbInit: true })
    }
  }

  handleChatToggle = () => {
    if (this.props.chatHasEnded === true) {
      return this.props.actions.toggleChat({ chatReset: true, chatHasEnded: false })
    }

    return this.props.actions.toggleChat({ chatIsHidden: false, chatIsMinimized: false })
  }

  // Check if is in XX:XX format
  checkTimeFormat = (time = '') => {
    return time.match(/^(\d{2}):(\d{2})$/) ? time : null
  }

  render () {
    const { alert, sidebar, chatOpeningHours, chatOnline, chatLoading, location } = this.props

    const bypassOpeningHours = location && location.query && location.query.bypassOpeningHours === 'true'
    const metaData = {
      title: 'Contact us | Go Argos',
      meta: [{
        name: 'description',
        content: 'How can we help?'
      }, {
        name: 'keywords',
        content: ''
      }, {
        name: 'canonical',
        content: 'https://www.argos.co.uk/help/contact-us/'
      }, {
        name: 'robots',
        content: 'index, follow'
      }]
    }

    const openingTimeCopy = chatOpeningHours && this.checkTimeFormat(chatOpeningHours.open)
    const closingTimeCopy = chatOpeningHours && this.checkTimeFormat(chatOpeningHours.lastChat)

    const socialAccordionHeader = (
      <div className='accordion__button accordion__button--social'>
        <EmailIcon height='25px' width='25px' className='accordion__icon' />
        <span>Message us</span>
      </div>
    )

    const telephoneAccordionHeader = (
      <div className='accordion__button accordion__button--telephone'>
        <PhoneIcon height='25px' width='25px' className='accordion__icon' />
        <span>Call us</span>
      </div>
    )

    const addressAccordionHeader = (
      <div className='accordion__button accordion__button--address'>
        <MyDetailsIcon height='25px' width='25px' className='accordion__icon' />
        <span>Write to us</span>
      </div>
    )

    const signVideoAccordionHeader = (
      <div className='accordion__button accordion__button--signvideo'>
        <SignLanguageIcon height='25px' width='25px' className='accordion__icon accordion__icon--signlanguage' />
        <span>SignVideo (BSL) </span>
      </div>
    )

    return (
      <div className='contact page container'>

        <div id='helpfaqcontent' tabIndex='-1' />
        <Helmet {...metaData} />

        <div className='xs-row xs-row--negative'>

          <Sidebar activePage='/help/contact-us/' links={sidebar} />

          <div className='xs-12 lg-9'>

            <h2 className='page__title'>Contact Us</h2>

            {
              alert && <div className={'contact__alert-container'}>
                <Alert backgroundColor='white' borderColor='red' textColor='red'>
                  <span dangerouslySetInnerHTML={{ __html: marked(alert) }} />
                </Alert>
              </div>
            }

            <SearchBar />
            <div className='xs-row'>
              <div className='xs-12 deflection'>
                <div className='page__content'>
                  <p><span className='font-bold'>Store information</span></p>
                  <p>Go to our <a href='/stores/'>store locator</a> page to find opening times and contact details.</p>
                  <p>You can use the availability checker on each product page to check whether an item is in stock at a certain store. For more information on using our availability checker, you can <a href='/help/faq/72458'>read our stock FAQs</a>.</p>
                  <p><span className='font-bold'>Frequently asked questions</span></p>
                  <p>For a quick answer to your question, take a look at the FAQs on our <a href='/help'>help page</a>.</p>
                  <p><span className='font-bold'>Argos Card information</span></p>
                  <p>If you have a question about your Argos Card, you’ll need to use the Argos Card app for support.</p>
                  <p><span className='font-bold'>Store and service updates</span></p>
                  <p>For updates on changes to our stores and services relating to the current Coronavirus (COVID-19) situation, you can visit our <a href='/help/coronavirus-service-impacts'>Coronavirus service impacts</a> page.</p>
                </div>
              </div>
            </div>

            <div className='xs-row xs-row--negative'>
              <div className='xs-12 md-6'>
                <div className='contact__panel'>

                  <div className='category-panel text-center'>
                    <div className='category-panel__spacer'>

                      {chatLoading &&
                        <LoadingWheel contained />
                      }

                      {!chatLoading && (chatOnline || bypassOpeningHours) &&
                        <React.Fragment>
                          <h2 className='category-panel__title category-panel__title--1'>
                            <ChatLogo online />
                            Chat with us
                          </h2>
                          <p className='category-panel__desc category-panel__message'>For a quick answer to your question, you can also take a look at our <a href='https://www.argos.co.uk/help'>help page</a>.</p>
                          <button className='contact__cta button button--purple button--half' onClick={this.handleChatToggle}>
                            Chat now
                          </button>
                        </React.Fragment>
                      }

                      {!chatLoading && !chatOnline && !bypassOpeningHours &&
                        <React.Fragment>
                          <h2 className='category-panel__title category-panel__title--1'>
                            <ChatLogo online={false} />
                            We are currently offline
                          </h2>

                          {chatOpeningHours && openingTimeCopy && closingTimeCopy &&
                            <p className='category-panel__desc'>
                              We're available everyday from {openingTimeCopy} to {closingTimeCopy}
                            </p>
                          }

                          <button disabled className='contact__cta button button--purple button--disabled button--half'>
                            Live chat now
                          </button>
                        </React.Fragment>
                      }

                    </div>
                  </div>

                </div>
              </div>
              <div className='xs-12 md-6'>
                <CategoryMenu setTile='1' />
              </div>
            </div>

            <div className='accordion'>
              <Accordion singleOpenOnly>
                <Accordion.Item hasBorderTop title={socialAccordionHeader}>
                  <div className='page__content'>
                    <p><button className='accordion__link' onClick={this.handleFacebookInit}>Connect on Facebook Messenger</button></p>
                    <p>You must already be logged into Facebook</p>
                    <p><a target='_blank' href='https://twitter.com/messages/compose?recipient_id=161296820'>Message us on Twitter</a></p>
                    <p>If you’ve already sent us a message on social media, there’s no need to message again. We’re really busy at the moment but we’re trying hard to get back to you as soon as we can.</p>
                  </div>
                </Accordion.Item>
                <Accordion.Item hasBorderTop title={telephoneAccordionHeader}>
                  <div className='page__content'>
                    <p className='indent'>0345 640 2020</p>
                    <p className='indent'>Available 7 days a week from 8am to 8pm.</p>
                    <p className='indent'>If you're calling about an order, please have your order number to hand. You can use our live chat service if you need to speak to someone quickly.</p>
                    <p><span className='font-bold'>Do you have difficulties hearing or speaking on the phone?</span></p>
                    <p>We also accept calls via Relay UK. Visit the <a href='https://www.relayuk.bt.com/' target='_blank'>Relay UK website</a> for more information about this service.</p>
                  </div>
                </Accordion.Item>
                <Accordion.Item hasBorderTop title={addressAccordionHeader}>
                  <div className='page__content'>
                    <p>Argos Limited <br />Royal Avenue <br />Widnes <br />WA8 8HS</p>
                  </div>
                </Accordion.Item>
                <Accordion.Item hasBorderTop hasBorderBottom title={signVideoAccordionHeader}>
                  <div className='page__content'>
                    <p>SignVideo instantly connects deaf customers to a British Sign Language (BSL) interpreter who phones the Customer Service centre and relays the conversation in real time. Please only use this service if you use British Sign Language and need to use our video interpretation service.</p>
                    <p><a href='https://argos.signvideo.net/'>Connect with SignVideo</a></p>
                    <p>Our SignVideo service is free to use and is available Monday to Friday between 8am to 6pm. For further information on SignVideo, you can watch this <a target='_blank' href='https://www.youtube.com/watch?v=JoPqz7bRzpU'>video</a> or email help@signvideo.co.uk</p>
                  </div>
                </Accordion.Item>
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

ContactUs.propTypes = {
  alert: PropTypes.string,
  location: PropTypes.shape({
    query: PropTypes.shape({
      bypassOpeningHours: PropTypes.string
    })
  }),
  sidebar: PropTypes.array,
  actions: PropTypes.object,
  chatHasEnded: PropTypes.bool,
  chatLoading: PropTypes.bool,
  chatOnline: PropTypes.bool,
  chatOpeningHours: PropTypes.object
}

function mapStateToProps (state) {
  return {
    alert: state.help.alert,
    sidebar: state.help.sidebar,
    chatHasEnded: state.page.chatHasEnded,
    chatLoading: state.page.chatLoading,
    chatOnline: state.page.chatOnline,
    chatOpeningHours: state.page.chatOpeningHours
  }
}

function mapDispatchToProps (dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

export {
  ContactUs
}

export default connect(mapStateToProps, mapDispatchToProps)(ContactUs)
